import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import "../assests/scss/components/_statusloader.scss";

const StatusLoader = ({
  show,
  status = "preparing",
  operation = "share",
  onComplete
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState(status);
  const intervalRef = useRef(null);

  // Status configurations with real, honest messages
  const statusConfig = {
    share: {
      preparing: {
        message: "Preparing events for sharing...",
        progress: 10,
        icon: "📋"
      },
      composing: {
        message: "Composing messages...",
        progress: 20,
        icon: "✍️"
      },
      generating_images: {
        message: "Generating posters...",
        progress: 40,
        icon: "🖼️"
      },
      generating_video: {
        message: "Generating video...",
        progress: 60,
        icon: "🎬"
      },
      uploading: {
        message: "Uploading to social media...",
        progress: 80,
        icon: "📤"
      },
      processing: {
        message: "Processing your request...",
        progress: 90,
        icon: "⚙️"
      },
      success: {
        message: "Events shared successfully!",
        progress: 100,
        icon: "✓"
      },
      error: {
        message: "Failed to share events",
        progress: 0,
        icon: "✕"
      }
    },
    download: {
      preparing: {
        message: "Preparing download...",
        progress: 20,
        icon: "📋"
      },
      generating: {
        message: "Generating file...",
        progress: 60,
        icon: "⚙️"
      },
      processing: {
        message: "Finalizing...",
        progress: 80,
        icon: "⚙️"
      },
      success: {
        message: "Download ready!",
        progress: 100,
        icon: "✓"
      },
      error: {
        message: "Download failed",
        progress: 0,
        icon: "✕"
      }
    },
    default: {
      preparing: {
        message: "Preparing...",
        progress: 30,
        icon: "⚙️"
      },
      processing: {
        message: "Processing...",
        progress: 70,
        icon: "⚙️"
      },
      success: {
        message: "Completed!",
        progress: 100,
        icon: "✓"
      },
      error: {
        message: "Operation failed",
        progress: 0,
        icon: "✕"
      }
    }
  };

  useEffect(() => {
    // Clear any existing interval when status changes
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (show) {
      setCurrentStatus(status);
      const config = statusConfig[operation] || statusConfig.default;
      const targetProgress = config[status]?.progress || 0;

      // Immediately set to target progress
      setProgress(targetProgress);

      // Auto-progression: increment 1% per second for 9 seconds
      // Stop at targetProgress + 9, or at 100%, whichever comes first
      const maxProgress = Math.min(targetProgress + 9, 100);

      // Only start auto-progression if we haven't reached 100%
      if (targetProgress < 100) {
        intervalRef.current = setInterval(() => {
          setProgress(prev => {
            if (prev >= maxProgress) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              return prev;
            }
            return prev + 1;
          });
        }, 2000); // 2000ms = 2.0 seconds, so 1% per 2.0 seconds
      }

      // Auto-hide on success after 2 seconds
      if (status === "success" && onComplete) {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } else {
      setProgress(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [show, status, operation, onComplete]);

  if (!show) return null;

  const config = statusConfig[operation] || statusConfig.default;
  const currentConfig = config[currentStatus] || config.preparing;

  return (
    <div className={`status-loader ${show ? 'status-loader--visible' : ''}`}>
      <div className={`status-loader__container status-loader__container--${currentStatus}`}>
        <div className="status-loader__content">
          {/* Icon */}
          <div className={`status-loader__icon status-loader__icon--${currentStatus}`}>
            {currentStatus === "preparing" || currentStatus === "composing" || currentStatus === "generating_images" || currentStatus === "generating_video" || currentStatus === "uploading" || currentStatus === "processing" || currentStatus === "generating" ? (
              <div className="status-loader__spinner">
                <svg viewBox="0 0 50 50">
                  <circle
                    className="status-loader__spinner-circle"
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    strokeWidth="4"
                  />
                </svg>
              </div>
            ) : (
              <span className="status-loader__icon-text">{currentConfig.icon}</span>
            )}
          </div>

          {/* Message */}
          <div className="status-loader__message">
            {currentConfig.message}
          </div>

          {/* Progress Bar */}
          {currentStatus !== "success" && currentStatus !== "error" && (
            <div className="status-loader__progress">
              <div className="status-loader__progress-bar">
                <div
                  className="status-loader__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="status-loader__progress-text">
                {progress}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    show: state.statusLoader?.show || false,
    status: state.statusLoader?.status || "preparing",
    operation: state.statusLoader?.operation || "default"
  };
};

export default connect(mapStateToProps, null)(StatusLoader);